/**
 * Start screen overlay (HANDOFF §6 Agent E / §3.8 / §8).
 *
 * Title + an AI difficulty selector (easy / normal / hard, default Normal) + a
 * "Tee Off" button. Calls `onStart(difficulty)` when the player begins; the host
 * (main.ts) uses the chosen difficulty to seed `createInitialState`. DOM/CSS with
 * §8 tokens; mobile-first portrait. No canvas drawing.
 */
import type { Difficulty } from '../sim/types.ts';

export interface StartScreenDeps {
  /** Called with the chosen difficulty when the player taps "Tee Off". */
  onStart(difficulty: Difficulty): void;
  /** Open the settings pop-up (gear button, top-right). Optional. */
  onSettings?: () => void;
  /** Start a real-time online duel in the given mode (ranked rates; casual doesn't). Optional. */
  onPlayOnline?(mode: 'ranked' | 'casual'): void;
  /** Start an endless local driving range (no opponent). Optional. */
  onPlayRange?: () => void;
  /** Open the card-pack shop. Optional. */
  onShop?: () => void;
  /** Open the collection gallery. Optional. */
  onCollection?: () => void;
}

export interface StartScreen {
  show(): void;
  hide(): void;
  /** Reflect the player's wallet (coins / gems). Shows the readout once called. */
  setWallet(coins: number, gems: number): void;
  /** Reflect the player's ranked rating. Shows the readout once called. */
  setRating(rating: number): void;
  readonly el: HTMLElement;
}

const DIFFICULTIES: readonly { key: Difficulty; label: string }[] = [
  { key: 'easy', label: 'Easy' },
  { key: 'normal', label: 'Normal' },
  { key: 'hard', label: 'Hard' },
];

/** Default selection per §6 acceptance criteria: Normal. */
const DEFAULT_DIFFICULTY: Difficulty = 'normal';

/**
 * Build the start overlay under `root`. The difficulty selector defaults to
 * Normal; the selection is held locally and passed to `onStart` on "Tee Off".
 */
export function createStartScreen(root: HTMLElement, deps: StartScreenDeps): StartScreen {
  let selected: Difficulty = DEFAULT_DIFFICULTY;

  const el = document.createElement('section');
  el.className = 'fc-overlay fc-overlay--start';

  const tag = document.createElement('p');
  tag.className = 'fc-overlay__tag';
  tag.textContent = 'Match play · lowest score wins';

  const title = document.createElement('h1');
  title.className = 'fc-overlay__title';
  title.innerHTML = 'Fairway<br /><span class="fc-overlay__title-accent">Clash</span>';

  const blurb = document.createElement('p');
  blurb.className = 'fc-overlay__blurb';
  blurb.textContent =
    'Pick a club, drag to aim, release to start the swing meter, then tap POWER and IMPACT. Read the green break — the ball rolls downhill after it lands.';

  // --- Difficulty selector (radio-group semantics, button UI) ---------------
  const diffWrap = document.createElement('div');
  diffWrap.className = 'fc-diff';
  diffWrap.setAttribute('role', 'radiogroup');
  diffWrap.setAttribute('aria-label', 'AI difficulty');

  const diffLabel = document.createElement('div');
  diffLabel.className = 'fc-diff__label';
  diffLabel.textContent = 'AI difficulty';
  diffWrap.appendChild(diffLabel);

  const diffRow = document.createElement('div');
  diffRow.className = 'fc-diff__row';

  const buttons = new Map<Difficulty, HTMLButtonElement>();
  function syncSelected(): void {
    for (const [key, btn] of buttons) {
      const on = key === selected;
      btn.classList.toggle('is-selected', on);
      btn.setAttribute('aria-checked', on ? 'true' : 'false');
    }
  }
  for (const { key, label } of DIFFICULTIES) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'fc-diff__opt';
    btn.setAttribute('role', 'radio');
    btn.textContent = label;
    btn.addEventListener('click', () => {
      selected = key;
      syncSelected();
    });
    buttons.set(key, btn);
    diffRow.appendChild(btn);
  }
  diffWrap.appendChild(diffRow);
  syncSelected();

  const play = document.createElement('button');
  play.type = 'button';
  play.className = 'fc-btn';
  play.textContent = 'Tee Off';
  play.addEventListener('click', () => deps.onStart(selected));

  el.append(tag, title, blurb, diffWrap, play);

  // Optional secondary modes (online duel + endless range). Sit under "Tee Off".
  const secondary = (label: string, handler: () => void): void => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'fc-btn';
    btn.style.cssText = 'margin-top:8px;background:transparent;border:1px solid currentColor;';
    btn.textContent = label;
    btn.addEventListener('click', handler);
    el.append(btn);
  };
  if (deps.onPlayOnline) {
    secondary('Ranked Duel ⚔', () => deps.onPlayOnline?.('ranked'));
    secondary('Casual Duel', () => deps.onPlayOnline?.('casual'));
  }
  if (deps.onPlayRange) secondary('Driving Range', () => deps.onPlayRange?.());
  if (deps.onShop) secondary('Card Packs 🎴', () => deps.onShop?.());
  if (deps.onCollection) secondary('Collection 🗂', () => deps.onCollection?.());

  // Settings gear (top-right of the overlay) — opens the full settings pop-up.
  if (deps.onSettings) {
    const gear = document.createElement('button');
    gear.type = 'button';
    gear.setAttribute('aria-label', 'Settings');
    gear.textContent = '⚙';
    gear.style.cssText =
      'position:absolute;top:calc(10px + env(safe-area-inset-top));right:14px;width:38px;height:38px;' +
      'border-radius:10px;border:1px solid #2c4255;background:rgba(8,16,22,.6);color:#f4efe2;font-size:20px;' +
      'line-height:1;display:flex;align-items:center;justify-content:center;cursor:pointer;z-index:2;';
    gear.addEventListener('click', () => deps.onSettings?.());
    el.appendChild(gear);
  }

  // Wallet (coins / gems) + rating, top-left — reflect the server economy / ranked profile.
  const wallet = document.createElement('div');
  wallet.style.cssText =
    'position:absolute;top:calc(12px + env(safe-area-inset-top));left:14px;display:none;gap:12px;' +
    'font-family:var(--font-pixel,monospace);font-size:10px;color:#f4efe2;z-index:2;align-items:center;' +
    'background:rgba(8,16,22,.55);padding:6px 11px;border-radius:9px;border:1px solid #2c4255;';
  el.appendChild(wallet);
  const ratingEl = document.createElement('div');
  ratingEl.style.cssText =
    'position:absolute;top:calc(42px + env(safe-area-inset-top));left:14px;display:none;' +
    'font-family:var(--font-pixel,monospace);font-size:10px;color:var(--gold,#f2c14e);z-index:2;' +
    'background:rgba(8,16,22,.55);padding:6px 11px;border-radius:9px;border:1px solid #2c4255;';
  el.appendChild(ratingEl);

  root.appendChild(el);

  return {
    el,
    show() {
      el.style.display = 'flex';
    },
    hide() {
      el.style.display = 'none';
    },
    setWallet(coins: number, gems: number) {
      wallet.textContent = `🪙 ${coins}    💎 ${gems}`;
      wallet.style.display = 'inline-flex';
    },
    setRating(rating: number) {
      ratingEl.textContent = `⚔ ${rating}`;
      ratingEl.style.display = 'block';
    },
  };
}
